import { Link } from 'react-router-dom';
import { HYPE_LIST, MOOD_LIST, RACKSAVANT, ROOM_CODE_LENGTH } from '@racksavant/core';
import { cx } from '../cx';
import { Reveal, Wipe } from '../components/Reveal';
import { ChaosField } from '../components/ChaosField';
import { DeviceDeck } from '../components/DeviceDeck';
import { FeatureArt, type FeatureArtKind } from '../components/FeatureArt';
import { FittingRoom } from '../components/FittingRoom';
import { MoodReadCard } from '../components/MoodReadCard';
import { ArrowRight } from '../components/icons';
import ui from '../ui.module.css';
import s from './FeaturesPage.module.css';

interface Feature {
  id: string;
  kicker: string;
  title: string;
  why: string[];
  points: Array<{ label: string; text: string }>;
  art: FeatureArtKind | 'read' | 'room';
}

const FEATURES: Feature[] = [
  {
    id: 'rack',
    kicker: 'The rack',
    title: 'Browse the way you actually decide.',
    why: [
      'Retail taxonomy was designed for warehouses. Category, size, colour, price — a schema that makes a lot of sense to a stockroom and none at all to a person standing in their room at eleven at night trying to work out who they want to be tomorrow.',
      'Starting from a feeling collapses the decision. Instead of scanning two hundred items for something that might work, you name the target — soft, sharp, feral, regal — and the rack rearranges itself around it. Fewer options, better ones, and no moment where you have to translate an emotion into a filter.',
    ],
    points: [
      {
        label: `${MOOD_LIST.length} moods`,
        text: 'each with an energy level and a warmth, which is also what tells the image model how to light you.',
      },
      {
        label: 'Occasion as a second axis',
        text: 'orthogonal to mood, because feral and work are not mutually exclusive.',
      },
      {
        label: 'Layering that knows the rules',
        text: 'a dress and a coat, yes. Two pairs of trousers, no. Build a full look, not one item.',
      },
    ],
    art: 'rack',
  },
  {
    id: 'studio',
    kicker: 'The studio',
    title: 'The wait is the best part, so we wrote it.',
    why: [
      'A generation takes twenty to forty seconds. That is either dead time or it is anticipation, and the difference is entirely in what you put on the screen. A progress bar makes people switch apps. A sequence of named stages — draping the fabric, finding the shoulder line, setting the lights — makes them stay and lean in.',
      'It is also honest. Each stage maps to something that is genuinely happening: pulling your body shot and the garments, the image edit, storing the result, then a second pass that reads the mood. You are watching real work, described in language borrowed from a photo studio rather than a queue system.',
    ],
    points: [
      {
        label: 'Live progress',
        text: 'the job streams over a websocket, so the stage on your screen is the stage on the server.',
      },
      {
        label: 'Queue while you wait',
        text: 'line up the next three fittings instead of watching one.',
      },
      {
        label: 'Reveal, unhurried',
        text: 'the finished look develops in rather than snapping in. It is a mirror, not a dashboard.',
      },
    ],
    art: 'develop',
  },
  {
    id: 'reads',
    kicker: 'Mood reads',
    title: 'A verdict, not a rating.',
    why: [
      'Nobody has ever been helped by a score out of ten. What people actually want after they try something on is language: a way to say what the thing is doing, so they can decide whether that is what they wanted it to do.',
      'So every look comes back with a written read. One verdict line, built to be screenshot. An energy number. Two to five adjectives it is genuinely giving. A colour story specific enough to act on. And a caption. Every sentence is about the garment. None of them are about you — that constraint is written into the prompt itself, not bolted on afterwards as a content filter.',
    ],
    points: [
      {
        label: 'Structured, not freeform',
        text: 'the read is typed data, which is why you can later search your looks by adjective.',
      },
      {
        label: 'Specific enough to use',
        text: 'one line of brass at the cuff beats great fit, every time.',
      },
      {
        label: 'Never about the body',
        text: 'no sizes, no measurements, no compliments on appearance. Ever.',
      },
    ],
    art: 'read',
  },
  {
    id: 'rooms',
    kicker: 'Fitting rooms',
    title: 'The group chat, standing in the mirror with you.',
    why: [
      'The honest version of how people shop online already involves four other people. You send a photo, you wait, you get back three heart emojis and one useful sentence twenty minutes later. The decision is social; the software just refuses to admit it.',
      `A fitting room is a live, ephemeral space with a ${ROOM_CODE_LENGTH}-character code. Everyone you invite sees each look land as it finishes developing. They react, they argue, they vote. You get the useful sentence while you still care about it, which is the entire difference between a fun evening and a returns label.`,
    ],
    points: [
      {
        label: 'Real time, not a thread',
        text: 'reactions and chat arrive over a live connection, in the room, on the look.',
      },
      {
        label: 'Ephemeral by design',
        text: `rooms expire after ${RACKSAVANT.roomTtlMs / 3_600_000} hours. Nothing becomes another archive to curate.`,
      },
      {
        label: 'Four free, twenty on Muse',
        text: 'big enough for a group chat, small enough to stay a group chat.',
      },
    ],
    art: 'room',
  },
  {
    id: 'hype',
    kicker: 'Hype',
    title: 'Reactions with an opinion inside them.',
    why: [
      `A like is a shrug with a colour. It tells you somebody saw the thing, and nothing else — which is why a feed full of them is oddly lonely. We replaced it with ${HYPE_LIST.length} reactions that each say something you could not have guessed: ${HYPE_LIST.map((h) => h.label.toLowerCase()).join(', ')}.`,
      'Each one is a photograph of a face rather than a glyph, because a face carries the tone and a glyph does not. The result is that hype is legible. Someone calling you a menace while someone else says soft launch it is an actual read on a look.',
    ],
    points: [
      {
        label: `${HYPE_LIST.length} kinds, no numbers`,
        text: 'the tally exists, but the phrase is what shows up in the feed.',
      },
      {
        label: 'It works on the Runway too',
        text: 'the same reactions on public looks from people you follow.',
      },
      {
        label: 'Notified once, gently',
        text: `hype batches. Your phone does not buzz ${HYPE_LIST.length} times.`,
      },
    ],
    art: 'hype',
  },
  {
    id: 'lookbooks',
    kicker: 'Lookbooks',
    title: 'A camera roll is not a story. An era is.',
    why: [
      'Everything you make ends up in the same undifferentiated scroll, and by week three you cannot find the good one. The problem is not storage, it is narrative — people do not remember their clothes as files, they remember them as periods. The Berlin week. Feral summer. The three months of quiet money.',
      'So the save action is name an era, not add to folder. A lookbook has a title, a cover, a mood, and an arc. It is the difference between an archive you never open and a thing you show someone.',
    ],
    points: [
      {
        label: 'Private or public',
        text: 'keep an era to yourself, or publish it as a page people can scroll.',
      },
      { label: 'Reorder by hand', text: 'sequence matters. A lookbook is edited, not sorted.' },
      {
        label: 'Cover, mood, and a count',
        text: 'enough structure to feel like an object, not enough to feel like admin.',
      },
    ],
    art: 'lookbooks',
  },
  {
    id: 'captions',
    kicker: 'Caption studio',
    title: 'The last ten percent that stops people posting.',
    why: [
      'The look is finished, it is good, and it sits in the drafts for six days because writing the caption feels like homework. That gap is where most content dies — not in the making, in the last small act of committing to a voice.',
      'The caption studio writes from the look itself: the garments, the mood, the colour story, the read. Six tones, from deadpan to devastating. Reroll until one sounds like you, then it copies with the image. It is not trying to sound clever. It is trying to remove the twenty minutes between finishing and posting.',
    ],
    points: [
      { label: 'Six tones', text: 'deadpan, soft launch, unhinged, poetic, blunt, devastating.' },
      {
        label: 'Written from the look',
        text: 'it has read the garments and the mood, so it never produces a generic line.',
      },
      { label: 'Copies with the export', text: 'image and caption on the clipboard together.' },
    ],
    art: 'captions',
  },
  {
    id: 'stylist',
    kicker: 'Savant',
    title: 'Ask the way you would ask a friend.',
    why: [
      'Real styling questions are never show me black dresses. They are wedding in October, not the bride, do not want to look like I tried — a knot of occasion, social risk and self-image that a filter cannot touch.',
      'Savant is a conversation over the rack. It reads what you say, pulls actual garments, explains the reasoning in one or two sentences and then puts the candidates straight into a fitting. It will disagree with you. That is the point of asking someone with taste rather than asking a search box.',
    ],
    points: [
      {
        label: 'It pulls real items',
        text: 'every suggestion is a garment you can fit immediately, not a description.',
      },
      {
        label: 'It gives a reason',
        text: 'short, specific, about the clothes. Never about the person wearing them.',
      },
      {
        label: 'It streams',
        text: 'answers arrive as they are written, so a long reply never feels like a wait.',
      },
    ],
    art: 'stylist',
  },
  {
    id: 'exports',
    kicker: 'Exports',
    title: 'Cut for the places you actually post.',
    why: [
      'A square PNG with a logo in the corner is a dead end. If the output does not already fit the surface you post on, you are opening a second app, and the odds of the thing ever going out drop by half.',
      'So exports come pre-cut. Vertical for stories, with the mood read burned in if you want it. A clean 4:5 for feed. A before-and-after split for the people who always ask. No watermark on paid plans, and the file names are readable so your camera roll stays navigable.',
    ],
    points: [
      { label: '9:16 story', text: 'with an optional mood-read overlay, typeset properly.' },
      { label: '4:5 feed post', text: 'the crop the platforms actually give you the most room for.' },
      { label: 'Before and after', text: 'the original and the look, split down the middle.' },
    ],
    art: 'exports',
  },
  {
    id: 'drops',
    kicker: 'Drops',
    title: 'Your taste, as something people can wear.',
    why: [
      'The thing an audience wants from a creator is not a link. It is the edit — the specific eight pieces you would actually put on, in the order you would put them on. Historically the only way to give them that was a photo and a caption saying where you got it.',
      'A drop is a set of garments you publish to your followers. They open it, put their own photo in it, and try the whole thing on themselves. You get to see which pieces landed. It is a lookbook that other people can stand inside, which is a categorically different object from a link in bio.',
    ],
    points: [
      {
        label: 'Upload your own garments',
        text: 'flat lays or product shots. Your pieces, your brand, on Studio.',
      },
      {
        label: 'Followers fit it themselves',
        text: 'the drop is a try-on, not a gallery. That is why people share it.',
      },
      {
        label: 'You see what worked',
        text: 'fittings, hype and saves per garment. Taste with a feedback loop.',
      },
    ],
    art: 'drops',
  },
];

const PAIR_DECK = ['rack-mobile', 'look-desktop'] as const;

function Art({ kind }: { kind: Feature['art'] }) {
  if (kind === 'read') return <MoodReadCard />;
  if (kind === 'room') return <FittingRoom plate="group" />;
  return <FeatureArt kind={kind} />;
}

export function FeaturesPage() {
  return (
    <>
      <section className={s.hero} aria-labelledby="features-title">
        <div className={ui.container}>
          <span className={ui.crop} aria-hidden="true" />
          <p className={ui.eyebrow}>Features</p>

          <h1 id="features-title" className={s.heroTitle}>
            <Wipe
              lines={[
                <span key="a" className={s.heroSmall}>
                  The studio does ten things
                </span>,
              ]}
            />
            <ChaosField text="on purpose" italic className={s.heroChaos} />
          </h1>

          <p className={cx(ui.lede, s.heroLede)}>
            None of this exists because it was on a roadmap. Each piece is here because of something
            people actually do — hesitate, ask a friend, screenshot, never post. What follows is the
            feature and the behaviour it is answering.
          </p>

          <ol className={s.contents}>
            {FEATURES.map((feature, i) => (
              <li key={feature.id}>
                <a href={`#${feature.id}`}>
                  <span aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                  {feature.kicker}
                </a>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className={s.deckSection} aria-labelledby="features-deck">
        <div className={cx(ui.container, s.deckGrid)}>
          <div>
            <p className={ui.eyebrow}>Running now</p>
            <h2 id="features-deck" className={ui.h3}>
              Two of the six surfaces, off the live app.
            </h2>
          </div>
          <DeviceDeck variant="pair" ids={[...PAIR_DECK]} />
        </div>
      </section>

      <div className={s.rows}>
        {FEATURES.map((feature, i) => (
          <section
            key={feature.id}
            id={feature.id}
            className={s.row}
            aria-labelledby={`${feature.id}-title`}
          >
            <div className={cx(ui.container, s.rowGrid, i % 2 === 1 && s.rowFlip)}>
              <Reveal className={s.rowCopy}>
                <p className={s.rowHead}>
                  <span className={s.index}>{String(i + 1).padStart(2, '0')}</span>
                  <span className={s.kicker}>{feature.kicker}</span>
                </p>
                <h2 id={`${feature.id}-title`} className={s.title}>
                  {feature.title}
                </h2>
                {feature.why.map((paragraph) => (
                  <p key={paragraph.slice(0, 40)} className={s.why}>
                    {paragraph}
                  </p>
                ))}
                <ul className={s.points}>
                  {feature.points.map((point) => (
                    <li key={point.label} className={s.point}>
                      <b>{point.label}</b>
                      {point.text}
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal className={s.rowArt} delay={90}>
                <Art kind={feature.art} />
              </Reveal>
            </div>
          </section>
        ))}
      </div>

      <section className={s.close} aria-labelledby="features-close">
        <div className={ui.container}>
          <h2 id="features-close" className={s.closeTitle}>
            All of it is free to try, today.
          </h2>
          <p className={cx(ui.lede, s.closeLede)}>
            Eight fittings a day, mood reads on every one of them, and rooms with up to four people.
            No card, no countdown.
          </p>
          <div className={s.closeCtas}>
            <Link to="/auth" className={cx(ui.btn, ui.primary)}>
              Open the studio
              <ArrowRight size={16} />
            </Link>
            <Link to="/pricing" className={cx(ui.btn, ui.ghost)}>
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
